import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createAutorivConfiguratorItem,
  deleteAutorivConfiguratorItem,
  getAutorivConfiguratorItem,
  listAutorivConfiguratorItemOptions,
  listAutorivConfiguratorItems,
  updateAutorivConfiguratorItem,
  type AutorivConfiguratorItem,
  type AutorivConfiguratorItemOptions,
  type CreateAutorivConfiguratorItemBody,
  type ListAutorivConfiguratorItemsParams,
  type PaginatedAutorivConfiguratorItems,
  type UpdateAutorivConfiguratorItemBody,
} from "../api/autorivConfiguratorItems";

const ROOT_KEY = "autoriv-configurator-items";

export function useAutorivConfiguratorItems(
  params: ListAutorivConfiguratorItemsParams,
) {
  return useQuery<PaginatedAutorivConfiguratorItems>({
    queryKey: [ROOT_KEY, "list", params],
    queryFn: () => listAutorivConfiguratorItems(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useAutorivConfiguratorItem(id: string | null) {
  return useQuery<AutorivConfiguratorItem>({
    queryKey: [ROOT_KEY, "detail", id],
    queryFn: () => getAutorivConfiguratorItem(id as string),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useAutorivConfiguratorItemOptions() {
  return useQuery<AutorivConfiguratorItemOptions>({
    queryKey: [ROOT_KEY, "options"],
    queryFn: () => listAutorivConfiguratorItemOptions(),
    staleTime: 5 * 60_000,
  });
}

export function useCreateAutorivConfiguratorItem() {
  const queryClient = useQueryClient();
  return useMutation<
    AutorivConfiguratorItem,
    Error,
    CreateAutorivConfiguratorItemBody
  >({
    mutationFn: (body) => createAutorivConfiguratorItem(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
}

export function useUpdateAutorivConfiguratorItem() {
  const queryClient = useQueryClient();
  return useMutation<
    AutorivConfiguratorItem,
    Error,
    { id: string; body: UpdateAutorivConfiguratorItemBody }
  >({
    mutationFn: ({ id, body }) => updateAutorivConfiguratorItem(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
}

/** Soft-delete via DELETE /v1/autoriv-configurator-items/:id. */
export function useDeleteAutorivConfiguratorItem() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: (id) => deleteAutorivConfiguratorItem(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
}
